import styled, { css } from 'styled-components';

import { CloseOutlineIc } from '@dsch/dd-icons';

import { POSITION, VARIANT } from './Tooltip.typed';
import type { Positions, Variants } from './Tooltip.typed';
import { ThemeTypes } from '../../../types/Theme.namespace';

/*
 * The tooltip arrow is a square rotated 45 degrees and half-obscured by the tooltip.
 * This value determines the length of the sides of that square.
 */
const ARROW_LENGTH = '12px';

const Container = styled.div`
  position: relative;
  z-index: ${({ theme }) => theme.donedeal.zIndices.TOOLTIP};
`;

const CloseIcon = styled(CloseOutlineIc)<{ variant: Variants }>`
  color: ${({ theme, variant }) =>
    variant === VARIANT.LIGHT
      ? theme.donedeal.colors.GREY_DARKER
      : theme.donedeal.colors.WHITE};
  cursor: pointer;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.S4};
`;

const HeaderText = styled.p<{
  fontSize: ThemeTypes.FontSize;
  fontWeight: ThemeTypes.FontWeight;
  variant: Variants;
}>`
  ${({ theme, fontSize }) => theme.donedeal.fontSize[fontSize]};
  font-weight: ${({ theme, fontWeight }) =>
    theme.donedeal.fontWeight[fontWeight]};
  color: ${({ theme, variant }) =>
    variant === VARIANT.LIGHT
      ? theme.donedeal.colors.GREY_DARKER
      : theme.donedeal.colors.WHITE};
`;

const Copy = styled.p<{ variant: Variants }>`
  ${({ theme }) => theme.donedeal.fontSize.M14};
  color: ${({ theme, variant }) =>
    variant === VARIANT.LIGHT
      ? theme.donedeal.colors.GREY_DARKER
      : theme.donedeal.colors.WHITE};
`;

const TooltipContainer = styled.div<{ position: Positions }>`
  position: absolute;
  z-index: ${({ theme }) => theme.donedeal.zIndices.DIALOG};
  top: ${({ position }) =>
    position === POSITION.BELOW ? `calc(100% + ${ARROW_LENGTH})` : ''};
  bottom: ${({ position }) =>
    position === POSITION.ABOVE ? `calc(100% + ${ARROW_LENGTH})` : ''};
`;

const lightBodyStyles = css`
  background-color: ${({ theme }) => theme.donedeal.colors.WHITE};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: ${({ theme }) => theme.shadow.LARGE};
`;

const darkBodyStyles = css`
  background-color: ${({ theme }) => theme.donedeal.colors.GREY_DARKER};
  border-radius: ${({ theme }) => theme.borderRadius.default};
`;

const TooltipBody = styled.div<{ variant: Variants }>`
  padding: ${({ theme }) => `${theme.spacing.S12} ${theme.spacing.M16}`};
  ${({ variant }) =>
    variant === VARIANT.LIGHT ? lightBodyStyles : darkBodyStyles};
`;

const Arrow = styled.div<{
  position: Positions;
  variant: Variants;
}>`
  position: absolute;
  height: ${ARROW_LENGTH};
  left: calc(50%);
  ${({ position }) =>
    position === POSITION.ABOVE
      ? `bottom: -${ARROW_LENGTH}`
      : `top: -${ARROW_LENGTH}`};

  &:after {
    content: '';
    height: ${ARROW_LENGTH};
    width: ${ARROW_LENGTH};
    position: absolute;
    top: ${({ position }) =>
      position === POSITION.ABOVE ? 'calc(-50%)' : 'calc(50%)'};
    left: calc(50%);
    background: ${({ theme, variant }) =>
      variant === VARIANT.LIGHT
        ? theme.donedeal.colors.WHITE
        : theme.donedeal.colors.GREY_DARKER};
    transform: rotate(45deg);
  }
`;

export {
  Container,
  CloseIcon,
  Header,
  HeaderText,
  Copy,
  TooltipContainer,
  TooltipBody,
  Arrow,
};
